import { useState, useCallback, useRef } from 'react'
import RootDashboards from './RootDashboards'

interface IFleetExecutionDashboardsProps {
  tasks: any[]
}

function extractRootPaths(tasks: any[]): string[] {
  const roots = new Set<string>()
  for (const task of tasks) {
    const input = task.machine_input || {}
    const root = input.s3_root_path || input.s3?.root || input.root
    if (root) roots.add(root)
  }
  return Array.from(roots)
}

export default function FleetExecutionDashboards({ tasks }: IFleetExecutionDashboardsProps) {
  const rootPaths = extractRootPaths(tasks)
  const [collected, setCollected] = useState<Record<string, any[]>>({})
  const callbacksRef = useRef<Record<string, (dashboards: any[]) => void>>({})

  const getOnLoaded = useCallback((rootPath: string) => {
    if (!callbacksRef.current[rootPath]) {
      callbacksRef.current[rootPath] = (dashboards: any[]) => {
        setCollected(prev => {
          if (prev[rootPath] === dashboards) return prev
          return { ...prev, [rootPath]: dashboards }
        })
      }
    }
    return callbacksRef.current[rootPath]
  }, [])

  if (rootPaths.length === 0) return null

  const allDashboards = Object.values(collected).flat()
  const seen = new Set<string>()
  const unique = allDashboards.filter(d => {
    if (seen.has(d.dashboard_id)) return false
    seen.add(d.dashboard_id)
    return true
  })
  const byType = new Map<string, any[]>()
  for (const d of unique) {
    const group = byType.get(d.dashboard_type) || []
    group.push(d)
    byType.set(d.dashboard_type, group)
  }

  return (
    <div className="dashboardsContainer">
      <h3>Dashboards</h3>
      {rootPaths.map((rp: string) => (
        <RootDashboards key={rp} root_path={rp} onLoaded={getOnLoaded(rp)} />
      ))}
      {Array.from(byType.entries()).map(([type, dashboards]) => (
        <div key={type} className="dashboardRootGroup">
          <h4 className="rootPathLabel">{type}</h4>
          <div className="dashboardCards">
            {dashboards.map(d => (
              <div key={d.dashboard_id} className="dashboardCard">
                <a rel="noreferrer" target="_blank" href={'/dashboard/' + d.dashboard_id}>
                  {(d.autogenerated ? '* ' : '') + (d.dashboard_label || d.dashboard_id)}
                </a>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
